import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Send, MessageSquare, Clock, Shield, RefreshCw, Package, Shirt } from 'lucide-react';
import StoreLayout from '../../components/store/StoreLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ScrollArea } from '../../components/ui/scroll-area';
import { useAuth } from '../../contexts/AuthContext';
import { conversationsAPI, publicAPI } from '../../lib/api';

export default function ConversationDetailPage() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [input, setInput] = useState('');
  const [productsData, setProductsData] = useState({});
  const [looksData, setLooksData] = useState({});
  const bottomRef = useRef(null);

  const statusBadge = (status) => {
    const normalized = (status || '').toUpperCase();
    const map = {
      OPEN: { label: 'Ouverte', variant: 'default' },
      ASSIGNED: { label: 'En cours', variant: 'secondary' },
      CLOSED: { label: 'Clôturée', variant: 'outline' }
    };
    const cfg = map[normalized] || map.OPEN;
    return <Badge variant={cfg.variant}>{cfg.label}</Badge>;
  };

  const loadConversation = useCallback(async () => {
    setLoading(true);
    try {
      const res = await conversationsAPI.get(conversationId);
      setConversation(res.data);
      const msgs = res.data.messages || [];
      setMessages(msgs);
      console.log('[DEBUG] Messages received:', msgs);
      
      // Check if messages have enriched products/looks already
      const hasEnrichedData = msgs.some(m => m.products?.length > 0 || m.looks?.length > 0);
      console.log('[DEBUG] Has enriched data:', hasEnrichedData);
      
      // Load product/look details for backward compatibility
      const allProductIds = [...new Set(msgs.flatMap(m => m.product_ids || []))];
      const allLookIds = [...new Set(msgs.flatMap(m => m.look_ids || []))];
      
      if (allProductIds.length > 0 || allLookIds.length > 0) {
        const [productsRes, looksRes] = await Promise.all([
          allProductIds.length > 0 ? publicAPI.getProducts({ limit: 100 }) : Promise.resolve({ data: { items: [] } }),
          allLookIds.length > 0 ? publicAPI.getLooks({ limit: 100 }) : Promise.resolve({ data: { items: [] } })
        ]);
        
        const prodMap = {};
        const lookMap = {};
        (productsRes.data.items || []).forEach(p => { prodMap[p.id] = p; });
        (looksRes.data.items || []).forEach(l => { lookMap[l.id] = l; });
        setProductsData(prodMap);
        setLooksData(lookMap);
      }
      
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    } catch (err) {
      console.error('Erreur chargement conversation', err);
      navigate('/account/messages');
    } finally {
      setLoading(false);
    }
  }, [conversationId, navigate]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadConversation();
  }, [user, loadConversation, navigate]);

  const isClosed = useMemo(() => (conversation?.status || '').toUpperCase() === 'CLOSED', [conversation]);

  const handleSend = async () => {
    const content = input.trim();
    if (!content || sending || isClosed) return;
    setSending(true);
    try {
      const res = await conversationsAPI.sendMessage(conversationId, content);
      setMessages((prev) => [...prev, res.data]);
      setInput('');
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    } catch (err) {
      console.error('Erreur envoi message', err);
    } finally {
      setSending(false);
    }
  };

  if (!user) {
    return null;
  }

  if (loading || !conversation) {
    return (
      <StoreLayout>
        <section className="py-10 px-4 sm:px-6 lg:px-8 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary" />
        </section>
      </StoreLayout>
    );
  }

  return (
    <StoreLayout>
      <section className="py-10 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/account/messages')}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Retour
          </Button>
          {statusBadge(conversation.status)}
          <Button variant="ghost" size="sm" onClick={loadConversation}>
            <RefreshCw className="h-4 w-4 mr-1" /> Actualiser
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-xl">{conversation.subject || 'Conversation'}</CardTitle>
            <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
              <span className="flex items-center gap-1"><MessageSquare className="h-4 w-4" /> {conversation.user_name}</span>
              <span className="flex items-center gap-1"><Clock className="h-4 w-4" /> {new Date(conversation.created_at).toLocaleString('fr-FR')}</span>
            </div>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg">
              <ScrollArea className="h-[60vh] p-4">
                <div className="space-y-4">
                  {messages.map((msg) => {
                    const isStaff = (msg.sender_role || '').toUpperCase() !== 'USER';
                    return (
                      <div key={msg.id} className={`flex ${isStaff ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[75%] rounded-lg p-3 text-sm border ${isStaff ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted text-foreground border-border'} `}>
                          <div className="flex items-center gap-2 text-xs opacity-80 mb-1">
                            {isStaff ? <Shield className="h-3 w-3" /> : <MessageSquare className="h-3 w-3" />}
                            <span>{isStaff ? 'Équipe' : conversation.user_name}</span>
                            <span>•</span>
                            <span>{new Date(msg.created_at).toLocaleString('fr-FR')}</span>
                          </div>
                          <div className="whitespace-pre-line leading-relaxed">{msg.content}</div>
                          {/* Product recommendations from staff */}
                          {msg.products?.length > 0 && (
                            <div className="mt-3 space-y-2">
                              {msg.products.map(prod => {
                                console.log('[DEBUG] Rendering product:', prod);
                                return (
                                  <Link
                                    key={prod.id}
                                    to={`/products/${prod.slug}`}
                                    className="flex gap-2 p-2 bg-background/10 hover:bg-background/20 rounded border transition"
                                  >
                                    {prod.image && (
                                      <img src={prod.image} alt={prod.name} className="w-16 h-16 object-cover rounded" />
                                    )}
                                    <div className="flex-1 flex flex-col justify-center text-xs">
                                      <p className="font-medium">{prod.name}</p>
                                      {prod.price_cents > 0 && (
                                        <p className="text-foreground/75">{(prod.price_cents / 100).toFixed(2)}€</p>
                                      )}
                                      <p className="text-foreground/50 mt-1">Voir le produit →</p>
                                    </div>
                                  </Link>
                                );
                              })}
                            </div>
                          )}
                          {/* Look recommendations from staff */}
                          {msg.looks?.length > 0 && (
                            <div className="mt-3 space-y-2">
                              {msg.looks.map(look => {
                                console.log('[DEBUG] Rendering look:', look);
                                return (
                                  <Link
                                    key={look.id}
                                    to={`/looks/${look.slug}`}
                                    className="flex gap-2 p-2 bg-background/10 hover:bg-background/20 rounded border transition"
                                  >
                                    {look.image && (
                                      <img src={look.image} alt={look.name} className="w-16 h-16 object-cover rounded" />
                                    )}
                                    <div className="flex-1 flex flex-col justify-center text-xs">
                                      <p className="font-medium">{look.name}</p>
                                      {look.bundle_price_cents > 0 && (
                                        <p className="text-foreground/75">{(look.bundle_price_cents / 100).toFixed(2)}€</p>
                                      )}
                                      <p className="text-foreground/50 mt-1">Voir le look →</p>
                                    </div>
                                  </Link>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  <div ref={bottomRef} />
                </div>
              </ScrollArea>

              <div className="border-t p-3 flex items-center gap-2">
                <Input
                  placeholder={isClosed ? 'Conversation clôturée' : 'Écrire un message...'}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  disabled={sending || isClosed}
                />
                <Button onClick={handleSend} disabled={sending || isClosed || !input.trim()}>
                  <Send className="h-4 w-4 mr-1" /> Envoyer
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>
    </StoreLayout>
  );
}
